import React from 'react';
import { Calendar, Globe, FileCheck, Landmark, Target, CheckCircle } from 'lucide-react';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { marketData } from '../../../data/terminalData';

interface StrategicCatalystsPanelProps {
  isExpanded?: boolean;
}

export const StrategicCatalystsPanel: React.FC<StrategicCatalystsPanelProps> = ({ isExpanded = false }) => {
  const { wto } = marketData;

  const catalysts = [
    {
      date: 'Mar 2026',
      event: 'WTO Full Membership',
      impact: 'Unlocks global trade access',
      icon: Globe,
      color: 'data-green',
    },
    {
      date: '2025',
      event: 'Eurobond Issuance',
      impact: '$1B+ sovereign bond',
      icon: Landmark,
      color: 'data-amber',
    },
    {
      date: '2026',
      event: 'IPO Pipeline',
      impact: 'Major state assets',
      icon: FileCheck,
      color: 'data-blue',
    },
    {
      date: '2027',
      event: 'Investment Grade',
      impact: 'S&P/Fitch trajectory',
      icon: Target,
      color: 'data-green',
    },
  ];

  const wtoStages = [
    { name: 'Application', done: true },
    { name: 'Working Party', done: true },
    { name: 'Bilateral', done: true },
    { name: 'Protocol', done: false },
    { name: 'Member', done: false },
  ];

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-data-green" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Strategic Catalysts</h2>
        </div>
        <span className="text-xs text-data-green font-semibold px-2 py-1 bg-data-green/10 rounded border border-data-green/20">
          4 CATALYSTS
        </span>
      </div>

      <div className="flex-1 p-5">
        {/* WTO Hero Section */}
        <div className="bg-gradient-to-b from-data-green/10 to-transparent rounded-lg p-4 border border-data-green/20 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-data-green" />
              <span className="text-sm font-bold text-white">WTO ACCESSION</span>
            </div>
            <span className="text-2xl font-mono font-black text-data-green">{wto.target}</span>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="h-3 bg-terminal-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-data-green/50 to-data-green rounded-full"
                style={{ width: `${wto.progress}%` }}
              />
            </div>
          </div>

          {/* Stage Markers */}
          <div className="flex justify-between">
            {wtoStages.map((stage, idx) => (
              <div key={stage.name} className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full mb-1 ${
                  stage.done
                    ? 'bg-data-green'
                    : idx === 3
                      ? 'bg-data-amber animate-pulse ring-2 ring-data-amber/30'
                      : 'bg-terminal-border'
                }`} />
                <span className={`text-xs ${stage.done ? 'text-data-green' : 'text-gray-500'}`}>
                  {stage.name}
                </span>
              </div>
            ))}
          </div>

          {/* Bilateral Progress */}
          <div className="mt-3 text-center">
            <span className="text-xs text-gray-400">
              Bilateral Agreements: <span className="text-data-green font-bold">{wto.bilateral.done}/{wto.bilateral.total}</span> completed
            </span>
          </div>
        </div>

        {/* Upcoming Catalysts */}
        <div className="space-y-3">
          {catalysts.slice(1).map((catalyst, idx) => (
            <div
              key={idx}
              className="flex items-center gap-4 p-3 bg-terminal-bg/60 rounded-lg border border-terminal-border/50"
            >
              <div className={`p-2.5 rounded-lg bg-${catalyst.color}/10 border border-${catalyst.color}/20`}>
                <catalyst.icon size={16} className={`text-${catalyst.color}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{catalyst.event}</span>
                  <span className={`text-sm font-mono font-bold text-${catalyst.color}`}>{catalyst.date}</span>
                </div>
                <span className="text-xs text-gray-500">{catalyst.impact}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );
};

export default StrategicCatalystsPanel;
